import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { theme } from '../../styles/theme';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import Badge from '../common/Badge';
import Loading from '../common/Loading';
import { 
  analyzeLaws, 
  checkLawExists, 
  analyzeReferences,
  checkSynthesisStatus,
  startSynthesis
} from '../../api/analysis';
import { saveAnalysis } from '../../api/chats';

const AnalysisWizard = ({ isOpen, onClose, chatId, onComplete }) => {
  // States
  const [step, setStep] = useState(1);
  const [topic, setTopic] = useState('');
  const [lawList, setLawList] = useState([]);
  const [newLaw, setNewLaw] = useState('');
  const [checkingLaw, setCheckingLaw] = useState(false);
  
  // Zustände für Synthese-Polling
  const [synthesisJobId, setSynthesisJobId] = useState(null);
  const [synthesisStatus, setSynthesisStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [synthesisResult, setSynthesisResult] = useState(null);
  const [pollingChatId, setPollingChatId] = useState(null);
  
  // Reset wizard state when closed
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep(1);
        setTopic('');
        setLawList([]);
        setNewLaw('');
        setSynthesisJobId(null);
        setSynthesisStatus(null);
        setSynthesisResult(null);
        setPollingChatId(null);
        // Stoppe das Polling, wenn Modal geschlossen wird
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
      }, 300); // Warte auf Modal-Schließ-Animation
    }
  }, [isOpen, pollingInterval]);

  // Laws analysis mutation
  const lawsMutation = useMutation({
    mutationFn: analyzeLaws,
    onSuccess: (data) => {
      setLawList(data.law_list || []);
    },
    onError: (error) => {
      // Verbesserte Fehlerbehandlung für den "string" Fehler
      const message = error.response?.data?.message || error.message;
      if (message === "string") {
        toast.error("Bitte präzisieren Sie Ihre Fragestellung. Die Anfrage war zu allgemein.");
      } else {
        toast.error(`Fehler: ${message}`);
      }
    }
  });
  
  // Check law exists mutation
  const checkLawMutation = useMutation({
    mutationFn: checkLawExists,
    onSuccess: (data) => {
      if (data.exists && !lawList.includes(data.lawAbbr)) {
        setLawList([...lawList, data.lawAbbr]);
      } else if (!data.exists) {
        toast.error(`Das Gesetz "${data.lawAbbr}" existiert nicht.`);
      }
      setNewLaw('');
      setCheckingLaw(false);
    },
    onError: () => {
      setCheckingLaw(false);
    },
  });
  
  // References analysis mutation (mit automatischem Synthese-Start)
  const referencesMutation = useMutation({
    mutationFn: analyzeReferences,
    onSuccess: (data) => {
      console.log("References API response:", data);
      
      // Prüfen, ob die Synthese automatisch gestartet wurde
      if (data.synthesisPipeline) {
        console.log("Synthesis pipeline started automatically:", data.synthesisPipeline);
        
        // Speichere Job-ID und Chat-ID für das Polling
        setSynthesisJobId(data.synthesisPipeline.jobId);
        setPollingChatId(data.synthesisPipeline.chatId);
        
        // Starte das Status-Polling
        startStatusPolling(data.synthesisPipeline.chatId);
      } else {
        console.warn("Automatic synthesis start not detected, we may need to start synthesis manually");
        
        // Fallback: Starte Synthese manuell, wenn automatische Synthese fehlgeschlagen ist
        if (chatId) {
          startSynthesisMutation.mutate({ chatId });
        }
      }
    },
  });
  
  // Manueller Synthese-Start (Fallback)
  const startSynthesisMutation = useMutation({
    mutationFn: startSynthesis,
    onSuccess: (data) => {
      console.log("Manual synthesis start response:", data);
      
      // Speichere Job-ID und starte Polling
      setSynthesisJobId(data.jobId);
      setPollingChatId(data.chatId || chatId);
      
      // Starte das Status-Polling
      startStatusPolling(data.chatId || chatId);
    },
  });
  
  // Synthese-Status-Abfrage
  const statusCheckMutation = useMutation({
    mutationFn: (checkChatId) => checkSynthesisStatus(checkChatId),
    onSuccess: (data) => {
      console.log("Synthesis status check:", data);
      
      // Aktualisiere den Status
      setSynthesisStatus(data.status);
      
      // Wenn die Synthese abgeschlossen ist
      if (data.status === 'completed' && data.result) {
        console.log("Synthesis completed with result:", data.result);
        
        // Speichere das Ergebnis und stoppe das Polling
        setSynthesisResult(data.result);
        
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        
        // Rufe den Callback auf
        if (onComplete) {
          onComplete(data.result);
        }
        
        // Schließe das Modal nach kurzer Verzögerung
        setTimeout(() => {
          onClose();
        }, 1500);
      }
      // Bei Fehler
      else if (data.status === 'error') {
        console.error("Synthesis error:", data.message);
        toast.error(`Fehler bei der Synthese: ${data.message}`);
        
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
      }
    },
  });
  
  // Save analysis mutation (unverändert)
  const saveAnalysisMutation = useMutation({
    mutationFn: (data) => saveAnalysis(data.chatId, {
      topic: data.topic,
      analysis: data.analysis,
      modelUsed: data.modelUsed
    }),
  });
  
  // Status-Polling starten
  const startStatusPolling = useCallback((statusChatId) => {
    if (!statusChatId) {
      console.error("Cannot start polling without a chat ID");
      return;
    }
    
    // Vorheriges Polling-Intervall beenden
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    
    // Status sofort einmal prüfen
    statusCheckMutation.mutate(statusChatId);
    
    // Polling-Intervall starten (alle 3 Sekunden)
    const interval = setInterval(() => {
      statusCheckMutation.mutate(statusChatId);
    }, 3000);
    
    setPollingInterval(interval);
    setPollingChatId(statusChatId);
  }, [pollingInterval, statusCheckMutation]);
  
  // Cleanup beim Unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);
  
  // Handle topic submit
  const handleAnalyzeTopic = () => {
    if (!topic.trim()) {
      return;
    }
    
    lawsMutation.mutate({ topic });
  };
  
  // Handle add law
  const handleAddLaw = () => {
    if (!newLaw.trim()) {
      return;
    }
    
    setCheckingLaw(true);
    checkLawMutation.mutate({ law: newLaw.trim() });
  };
  
  // Handle remove law
  const handleRemoveLaw = (law) => {
    setLawList(lawList.filter(item => item !== law));
  };
  
  // Continue to references and start analysis
  const handleContinueToReferences = () => {
    if (lawList.length === 0) {
      return;
    }
    
    setStep(2);
    
    const referencesData = {
      topic,
      law_list: lawList,
      chatId: chatId // Chat-ID übergeben für automatische Synthese
    };
    
    referencesMutation.mutate(referencesData);
  };
  
  // Check if any mutation is loading
  const isLoading = 
    lawsMutation.isPending || 
    checkLawMutation.isPending || 
    referencesMutation.isPending || 
    startSynthesisMutation.isPending ||
    statusCheckMutation.isPending ||
    saveAnalysisMutation.isPending;
  
  // Check if we should disable close
  const disableClose = isLoading;
  
  // Erster Schritt: Thema eingeben und Gesetze auswählen
  const renderStepOne = () => (
    <>
      <Section>
        <SectionTitle>Rechtliche Fragestellung</SectionTitle>
        <Input
          id="topic"
          placeholder="Beschreiben Sie Ihre rechtliche Fragestellung..."
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          disabled={lawsMutation.isPending || lawList.length > 0}
          fullWidth
        />
        
        {lawList.length === 0 && (
          <ButtonContainer>
            <Button
              onClick={handleAnalyzeTopic}
              isLoading={lawsMutation.isPending}
              disabled={!topic.trim()}
            >
              Relevante Gesetze identifizieren
            </Button>
          </ButtonContainer>
        )}
      </Section>
      
      {(lawList.length > 0 || lawsMutation.isPending) && (
        <Section>
          <SectionTitle>Relevante Gesetze</SectionTitle>
          
          {lawsMutation.isPending ? (
            <Loading text="Identifiziere relevante Gesetze..." />
          ) : (
            <>
              <LawListContainer>
                {lawList.map((law) => (
                  <Badge
                    key={law}
                    variant="primary"
                    removable
                    onRemove={() => handleRemoveLaw(law)}
                  >
                    {law}
                  </Badge>
                ))}
                
                {lawList.length === 0 && (
                  <EmptyState>Keine Gesetze vorhanden</EmptyState>
                )}
              </LawListContainer>
              
              <AddLawContainer>
                <Input
                  id="newLaw"
                  placeholder="Gesetz hinzufügen (z.B. ABGB)"
                  value={newLaw}
                  onChange={(e) => setNewLaw(e.target.value)}
                  disabled={checkingLaw}
                />
                <Button
                  onClick={handleAddLaw}
                  isLoading={checkingLaw}
                  disabled={!newLaw.trim()}
                  size="small"
                >
                  Hinzufügen
                </Button>
              </AddLawContainer>
              
              <ButtonContainer>
                <Button
                  onClick={handleContinueToReferences}
                  disabled={lawList.length === 0}
                >
                  Analyse starten
                </Button>
              </ButtonContainer>
            </>
          )}
        </Section>
      )}
    </>
  );
  
  // Zweiter Schritt: Asynchrone Analyse mit Statusanzeige
  const renderStepTwo = () => (
    <>
      <Section>
        <SectionTitle>Analyse wird durchgeführt</SectionTitle>
        <StepProgress>
          {referencesMutation.isPending ? (
            <Loading text="Identifiziere relevante Gesetzesabschnitte und Urteile..." />
          ) : synthesisStatus === 'synthesis_in_progress' || synthesisStatus === 'synthesis_started' ? (
            <Loading text="Erstelle rechtliche Synthese..." />
          ) : synthesisStatus === 'completed' ? (
            <SuccessMessage>
              <IconCheck />
              <span>Analyse erfolgreich abgeschlossen!</span>
            </SuccessMessage>
          ) : synthesisStatus === 'error' ? (
            <ErrorMessage>
              <IconError />
              <span>Fehler bei der Analyse. Bitte versuchen Sie es später erneut.</span>
            </ErrorMessage>
          ) : (
            <Loading text="Initialisiere Analyse..." />
          )}
        </StepProgress>
        
        {/* Status-Details anzeigen */}
        {synthesisStatus && (
          <StatusDetails>
            <StatusLabel>Status: </StatusLabel>
            <StatusValue status={synthesisStatus}>
              {synthesisStatus === 'synthesis_in_progress' ? 'Synthese wird erstellt' : 
               synthesisStatus === 'synthesis_started' ? 'Synthese gestartet' :
               synthesisStatus === 'completed' ? 'Abgeschlossen' :
               synthesisStatus === 'error' ? 'Fehler' :
               synthesisStatus}
            </StatusValue>
            
            {synthesisJobId && (
              <>
                <StatusLabel>Job-ID: </StatusLabel>
                <StatusValue>{synthesisJobId}</StatusValue>
              </>
            )}
          </StatusDetails>
        )}
      </Section>
    </>
  );
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={disableClose ? undefined : onClose}
      title="Rechtliche Analyse"
      showCloseButton={!disableClose}
      closeOnOverlayClick={!disableClose}
      size="large"
    >
      <Container>
        <StepIndicator>
          <Step active={step === 1} completed={step > 1}>
            <StepNumber>{step > 1 ? '✓' : '1'}</StepNumber>
            <StepLabel>Thema & Gesetze</StepLabel>
          </Step>
          <StepSeparator completed={step > 1} />
          <Step active={step === 2} completed={step > 2}>
            <StepNumber>{step > 2 ? '✓' : '2'}</StepNumber>
            <StepLabel>Analyse & Synthese</StepLabel>
          </Step>
        </StepIndicator>
        
        <Content>
          {step === 1 && renderStepOne()}
          {step === 2 && renderStepTwo()}
        </Content>
      </Container>
    </Modal>
  );
};

// Styled Components (größtenteils unverändert)
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  opacity: ${({ active, completed }) => (active || completed ? 1 : 0.5)};
`;

const StepNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${({ active, completed }) => 
    completed ? theme.colors.success : 
    active ? theme.colors.primary : 
    theme.colors.disabled};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 500;
  margin-right: 0.5rem;
`;

const StepLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
`;

const StepSeparator = styled.div`
  flex: 1;
  height: 2px;
  background-color: ${({ completed }) => 
    completed ? theme.colors.success : theme.colors.disabled};
  margin: 0 0.75rem;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: ${theme.colors.heading};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const LawListContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  min-height: 36px;
`;

const AddLawContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const StepProgress = styled.div`
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyState = styled.div`
  color: ${theme.colors.textLight};
  font-size: 0.875rem;
  font-style: italic;
`;

// Neue Styled Components für die Status-Anzeige
const StatusDetails = styled.div`
  background-color: ${theme.colors.backgroundAlt};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  margin-top: 1rem;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.5rem;
  align-items: center;
`;

const StatusLabel = styled.div`
  font-weight: 500;
  color: ${theme.colors.textLight};
  font-size: 0.875rem;
`;

const StatusValue = styled.div`
  font-family: monospace;
  color: ${({ status }) => 
    status === 'completed' ? theme.colors.success :
    status === 'error' ? theme.colors.error :
    theme.colors.text};
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${theme.colors.success};
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${theme.colors.error};
  font-weight: 500;
`;

// Icons
const IconCheck = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const IconError = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
);

export default AnalysisWizard;